import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import NumericKeypad from '@/components/NumericKeypad/NumericKeypad'
import { setLoyaltyCard } from '../../store/checkoutSlice'

const LoyaltyCard = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const loyaltyCard = useSelector(state => state.checkout.loyaltyCard)
  const [inputValue, setInputValue] = useState('')
  const [showInput, setShowInput] = useState(false)

  const handleSubmit = () => {
    if (inputValue.trim()) {
      dispatch(setLoyaltyCard(inputValue.trim()))
      setShowInput(false)
      setInputValue('')
    }
  }

  const handleCancel = () => {
    setShowInput(false)
    setInputValue('')
  }

  const handleRemove = () => {
    dispatch(setLoyaltyCard(null))
  }

  if (loyaltyCard) {
    return (
      <Wrapper>
        <LoyaltyRow>
          <LoyaltyInfo>
            <LoyaltyIcon>
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 3.5l2.6 5.3 5.8.8-4.2 4.1 1 5.8L12 16.8l-5.2 2.7 1-5.8-4.2-4.1 5.8-.8z" />
              </svg>
            </LoyaltyIcon>
            <LoyaltyText>
              <LoyaltyLabel>{t('loyalty.applied')}</LoyaltyLabel>
              <LoyaltyNumber>•••• {loyaltyCard.slice(-4)}</LoyaltyNumber>
            </LoyaltyText>
          </LoyaltyInfo>
          <DiscountBadge>-10%</DiscountBadge>
          <RemoveBtn onClick={handleRemove} aria-label={t('cartPanel.remove')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M7 7l10 10M17 7L7 17" />
            </svg>
          </RemoveBtn>
        </LoyaltyRow>
      </Wrapper>
    )
  }

  return (
    <>
      <Wrapper>
        <ScanPrompt onClick={() => setShowInput(true)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
            <path d="M2.5 9.5h19M6.5 15h4" />
          </svg>
          <span>{t('loyalty.prompt')}</span>
        </ScanPrompt>
      </Wrapper>

      {showInput && (
        <PopupBackdrop onClick={handleCancel}>
          <PopupModal onClick={(e) => e.stopPropagation()}>
            <PopupTitle>{t('loyalty.numberTitle')}</PopupTitle>
            <LoyaltyDisplay $empty={!inputValue}>
              {inputValue || t('loyalty.enterNumber')}
            </LoyaltyDisplay>
            <NumericKeypad
              value={inputValue}
              onChange={setInputValue}
              onSubmit={handleSubmit}
            />
            <PopupCancelBtn onClick={handleCancel}>{t('loyalty.cancel')}</PopupCancelBtn>
          </PopupModal>
        </PopupBackdrop>
      )}
    </>
  )
}

export default LoyaltyCard

const Wrapper = styled.div`
  padding: 16px 24px 0;
  border-top: 1px solid var(--color-line);
  flex-shrink: 0;

  @media (max-width: 1600px) {
    padding-top: 8px;
  }
`

const ScanPrompt = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  padding: 0 16px;
  border: 1px dashed ${({ theme }) => theme.colors.primary}88;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.primary}0D;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primaryText || theme.colors.primary};
  transition: background 0.1s, transform 0.1s;
  -webkit-tap-highlight-color: transparent;

  svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:active {
    background: ${({ theme }) => theme.colors.primary}1F;
    transform: scale(0.99);
  }
`

const PopupBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(16, 24, 32, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1000;
`

const PopupModal = styled.div`
  background: #ffffff;
  border-radius: var(--radius-card, 14px);
  padding: 24px;
  width: 380px;
  max-width: 100%;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  box-shadow: 0 24px 64px rgba(16, 24, 32, 0.22);
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const PopupTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--color-text);
  text-align: center;
`

const LoyaltyDisplay = styled.div`
  min-height: 56px;
  padding: 0 16px;
  border: 1px solid ${({ $empty, theme }) => $empty ? 'var(--color-line)' : theme.colors.primary};
  border-radius: 12px;
  background: var(--color-body);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-family: inherit;
  font-size: ${({ $empty }) => $empty ? '1rem' : '1.4rem'};
  font-weight: ${({ $empty }) => $empty ? 400 : 700};
  font-variant-numeric: tabular-nums;
  letter-spacing: ${({ $empty }) => $empty ? '0' : '0.08em'};
  color: ${({ $empty, theme }) => $empty ? 'var(--color-muted)' : 'var(--color-text)'};
  word-break: break-all;
`

const PopupCancelBtn = styled.button`
  min-height: 48px;
  margin-top: 4px;
  border: 1px solid var(--color-line);
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  font-family: inherit;
  color: var(--color-muted);
  transition: background 0.1s;

  &:active { background: var(--color-line); }
`

const LoyaltyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 56px;
  padding: 4px 4px 4px 12px;
  border: 1px solid rgba(46, 158, 107, 0.22);
  border-radius: 12px;
  background: var(--color-success-soft);
`

const LoyaltyInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`

const LoyaltyIcon = styled.span`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-success);
  color: #ffffff;

  svg {
    width: 16px;
    height: 16px;
  }
`

const LoyaltyText = styled.div`
  min-width: 0;
`

const LoyaltyLabel = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--color-success);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const LoyaltyNumber = styled.div`
  font-size: 0.8rem;
  color: var(--color-muted);
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.06em;
`

const DiscountBadge = styled.span`
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--color-success);
  color: #ffffff;
  font-size: 0.85rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`

const RemoveBtn = styled.button`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  padding: 0;
  border: none;
  border-radius: 12px;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-muted);
  transition: background 0.1s;
  -webkit-tap-highlight-color: transparent;

  svg {
    width: 20px;
    height: 20px;
  }

  &:active { background: var(--color-line); }
`
